import { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { Trash, UserPlus, Cube, User, PencilSimple, EnvelopeSimple, Crown, ClockCounterClockwise, ShareNetwork } from '@phosphor-icons/react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from '@/components/ui/dialog';
import useDirectory from '@/hooks/useDirectory';
import { useAuth, isAdmin } from '@/auth/AuthContext';
import { itApi } from './itApi';
import { Credential } from './StaffProfile';
import { formatDate } from '@/utils/datetime';

const yen = (n) => (typeof n === 'number' ? `¥${n.toLocaleString('ja-JP', { timeZone: 'Asia/Tokyo' })}` : '—');
const fmtDate = (s) => (s ? formatDate(s) : '—');

export default function LicenseSeatsDialog({ open, onClose, licenseId, onChanged }) {
  const { employees } = useDirectory();
  const { user } = useAuth();
  const admin = isAdmin(user);
  const [lic, setLic] = useState(null);
  const [seats, setSeats] = useState([]);
  const [revealLog, setRevealLog] = useState(null);
  const [assets, setAssets] = useState([]);
  const [targetType, setTargetType] = useState('employee');
  const [targetId, setTargetId] = useState('');
  const [newEmail, setNewEmail] = useState('');
  const [busy, setBusy] = useState(false);
  const [editSeat, setEditSeat] = useState(null); // { id, login_email, password }

  const load = async () => {
    if (!licenseId) return;
    try {
      const [l, s] = await Promise.all([itApi.license(licenseId), itApi.licenseSeats(licenseId)]);
      setLic(l); setSeats(s);
    } catch { toast.error('読み込みに失敗しました'); }
  };

  useEffect(() => {
    if (open) {
      setTargetId(''); setTargetType('employee'); setNewEmail(''); setEditSeat(null); setRevealLog(null);
      load(); itApi.assets().then(setAssets).catch(() => {});
      if (admin) itApi.licenseRevealLog(licenseId).then(setRevealLog).catch(() => setRevealLog([]));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, licenseId]);

  const add = async () => {
    if (!targetId) { toast.error('割当先を選択してください'); return; }
    setBusy(true);
    try {
      await itApi.assignSeat(licenseId, { target_type: targetType, target_id: targetId, login_email: newEmail || null });
      toast.success('割り当てました'); setTargetId(''); setNewEmail('');
      await load(); onChanged?.();
    } catch (err) { toast.error(err?.response?.data?.detail || '割当に失敗しました'); }
    finally { setBusy(false); }
  };

  const release = async (seatId) => {
    setBusy(true);
    try {
      await itApi.releaseSeat(licenseId, seatId);
      toast.success('解除しました');
      await load(); onChanged?.();
    } catch { toast.error('解除に失敗しました'); } finally { setBusy(false); }
  };

  const toggleAdmin = async (s) => {
    setBusy(true);
    try {
      await itApi.updateSeat(licenseId, s.id, { is_admin: !s.is_admin });
      toast.success(s.is_admin ? '管理者を解除しました' : '管理者に設定しました');
      await load(); onChanged?.();
    } catch (err) { toast.error(err?.response?.data?.detail || '更新に失敗しました'); }
    finally { setBusy(false); }
  };

  const saveSeat = async () => {
    if (!editSeat) return;
    setBusy(true);
    try {
      const body = { login_email: editSeat.login_email || null };
      if (editSeat.password) body.password = editSeat.password;
      await itApi.updateSeat(licenseId, editSeat.id, body);
      toast.success('座席の認証情報を更新しました');
      setEditSeat(null);
      await load();
    } catch (err) { toast.error(err?.response?.data?.detail || '更新に失敗しました'); }
    finally { setBusy(false); }
  };

  const options = targetType === 'employee' ? employees : assets;

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-xl max-h-[88vh] overflow-y-auto" aria-describedby={undefined} data-testid="license-seats-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            {lic?.name || 'ライセンス'}
            {lic && <Badge variant="outline" className="bg-blue-50 text-blue-700 border-blue-200">{lic.type_label}</Badge>}
          </DialogTitle>
        </DialogHeader>
        {lic && (
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-2 text-sm rounded-lg border border-zinc-200 p-3">
              <div><span className="text-zinc-500 text-xs">ベンダー</span><div>{lic.vendor || '—'}</div></div>
              <div><span className="text-zinc-500 text-xs">金額</span><div>{yen(lic.cost)}</div></div>
              <div><span className="text-zinc-500 text-xs">更新期限</span><div>{fmtDate(lic.expiry_date)}</div></div>
              <div><span className="text-zinc-500 text-xs">座席</span><div className="font-semibold">{lic.seats_used}{lic.seats_total ? ` / ${lic.seats_total}` : ' / 無制限'}</div></div>
              {lic.license_key && <div className="col-span-2"><span className="text-zinc-500 text-xs">キー</span><div className="font-mono text-xs break-all">{lic.license_key}</div></div>}
              <div className="col-span-2">
                <span className="text-zinc-500 text-xs">ログイン情報の共有先</span>
                <div className="inline-flex items-center gap-1 text-xs" data-testid="license-seats-sharing">
                  <ShareNetwork size={13} className="text-teal-600" />
                  {lic.is_shared
                    ? `${(lic.shared_employee_ids || []).length}名・${(lic.shared_department_ids || []).length}部署に共有中`
                    : 'IT管理者のみ（共有なし）'}
                </div>
              </div>
            </div>

            {admin && (
              <div className="rounded-lg border border-zinc-200 p-3" data-testid="license-reveal-log">
                <p className="text-xs font-semibold text-zinc-600 inline-flex items-center gap-1.5">
                  <ClockCounterClockwise size={14} /> ログイン情報の表示・コピー履歴
                </p>
                {revealLog === null ? <p className="mt-1 text-[11px] text-zinc-400">読み込み中…</p>
                  : revealLog.length === 0 ? <p className="mt-1 text-[11px] text-zinc-400" data-testid="license-reveal-log-empty">まだ履歴はありません</p>
                    : (
                      <ul className="mt-1.5 space-y-1">
                        {revealLog.map((r, i) => (
                          <li key={i} className="flex items-center justify-between gap-2 text-[11px] text-zinc-600" data-testid={`license-reveal-log-row-${i}`}>
                            <span className="truncate"><span data-no-translate>{r.actor_name || r.actor_email || '—'}</span> — {r.action_label}</span>
                            <span className="shrink-0 text-zinc-400">{fmtDate(r.created_at)}</span>
                          </li>
                        ))}
                      </ul>
                    )}
              </div>
            )}

            <div className="rounded-lg bg-zinc-50 border border-zinc-200 p-3 space-y-2" data-testid="license-seat-add">
              <p className="text-xs font-semibold text-zinc-600 flex items-center gap-1.5"><UserPlus size={15} weight="bold" /> 座席を割り当て</p>
              <div className="flex flex-col sm:flex-row gap-2">
                <Select value={targetType} onValueChange={(v) => { setTargetType(v); setTargetId(''); }}>
                  <SelectTrigger data-testid="license-seat-type" className="h-10 sm:w-32"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="employee">社員</SelectItem>
                    <SelectItem value="asset">資産</SelectItem>
                  </SelectContent>
                </Select>
                <Select value={targetId} onValueChange={setTargetId}>
                  <SelectTrigger data-testid="license-seat-target" className="h-10 flex-1"><SelectValue placeholder={targetType === 'employee' ? '社員を選択' : '資産を選択'} /></SelectTrigger>
                  <SelectContent>
                    {options.map((o) => <SelectItem key={o.id} value={o.id}>{o.name}{o.asset_tag ? `（${o.asset_tag}）` : ''}</SelectItem>)}
                  </SelectContent>
                </Select>
                <Button data-testid="license-seat-add-btn" disabled={busy} onClick={add} className="h-10 bg-teal-700 hover:bg-teal-800">追加</Button>
              </div>
              <Input value={newEmail} onChange={(e) => setNewEmail(e.target.value)} placeholder="この座席のログインメール（空欄=社員のメールを自動入力）"
                data-testid="license-seat-new-email" className="h-9 text-sm" />
              <p className="text-[10px] text-zinc-400">空欄のまま追加すると社員台帳のメールを自動入力します（後から編集可能）。パスワードは本人が「マイデバイス」から登録できます。</p>
            </div>

            <div data-testid="license-seat-list">
              <div className="flex flex-wrap items-center justify-between gap-2 mb-2">
                <p className="text-xs font-semibold text-zinc-600">割当一覧（{seats.length}）</p>
                <p className="text-xs text-amber-700 inline-flex items-center gap-1" data-testid="license-seats-admin-line">
                  <Crown size={13} weight="fill" className="text-amber-500" /> 管理者:{' '}
                  <span className="font-semibold">{seats.filter((s) => s.is_admin).map((s) => s.target_label).join('、') || '未設定'}</span>
                </p>
              </div>
              {seats.length === 0 ? (
                <p className="text-xs text-zinc-400">まだ割り当てられていません</p>
              ) : (
                <ul className="space-y-1.5">
                  {[...seats].sort((a, b) => (b.is_admin ? 1 : 0) - (a.is_admin ? 1 : 0)).map((s) => (
                    <li key={s.id} className="text-sm border border-zinc-100 rounded-lg px-3 py-2" data-testid={`license-seat-${s.id}`}>
                      <div className="flex items-center justify-between gap-2">
                        <span className="flex items-center gap-2 min-w-0">
                          {s.target_type === 'asset' ? <Cube size={16} className="text-zinc-400 shrink-0" /> : <User size={16} className="text-zinc-400 shrink-0" />}
                          <span className="truncate">{s.target_label}</span>
                          {s.is_admin
                            ? <Badge variant="outline" className="bg-amber-50 text-amber-700 border-amber-200 text-[10px] shrink-0" data-testid={`license-seat-admin-badge-${s.id}`}><Crown size={10} weight="fill" className="mr-0.5" /> 管理者</Badge>
                            : <Badge variant="outline" className="text-[10px] shrink-0">{s.target_type === 'asset' ? '資産' : 'メンバー'}</Badge>}
                        </span>
                        <span className="flex items-center gap-1 shrink-0">
                          <Button size="sm" variant="ghost" data-testid={`license-seat-admin-toggle-${s.id}`}
                            disabled={busy} onClick={() => toggleAdmin(s)}
                            className={`h-7 px-2 ${s.is_admin ? 'text-amber-600 hover:bg-amber-50' : 'text-zinc-400 hover:bg-zinc-100'}`}
                            title={s.is_admin ? '管理者を解除' : 'このライセンスの管理者にする'}><Crown size={15} weight={s.is_admin ? 'fill' : 'regular'} /></Button>
                          <Button size="sm" variant="ghost" data-testid={`license-seat-edit-${s.id}`}
                            onClick={() => setEditSeat(editSeat?.id === s.id ? null : { id: s.id, login_email: s.login_email || '', password: '' })}
                            className="h-7 px-2 text-zinc-500 hover:bg-zinc-100" title="座席の認証情報を編集"><PencilSimple size={15} /></Button>
                          <Button size="sm" variant="ghost" data-testid={`license-seat-release-${s.id}`} onClick={() => release(s.id)} className="h-7 px-2 text-red-600 hover:bg-red-50"><Trash size={15} /></Button>
                        </span>
                      </div>
                      <div className="mt-1 flex items-center justify-between gap-2 flex-wrap">
                        <span className="inline-flex items-center gap-1 text-[11px] text-zinc-500 min-w-0">
                          <EnvelopeSimple size={12} className="shrink-0" />
                          {s.login_email
                            ? <span className="font-mono truncate" data-testid={`license-seat-email-${s.id}`}>{s.login_email}</span>
                            : <span className="text-zinc-300">ログインメール未設定</span>}
                        </span>
                        {admin
                          ? <Credential hasPassword={s.has_password} onReveal={() => itApi.revealSeat(licenseId, s.id).then((r) => r.password)} testid={`license-seat-${s.id}`} />
                          : (s.has_password && <span className="text-[11px] text-zinc-400">パスワード登録済み（本人管理）</span>)}
                      </div>
                      {editSeat?.id === s.id && (
                        <div className="mt-2 rounded-lg bg-zinc-50 border border-zinc-200 p-2 space-y-2" data-testid={`license-seat-edit-form-${s.id}`}>
                          <Input value={editSeat.login_email} onChange={(e) => setEditSeat({ ...editSeat, login_email: e.target.value })}
                            placeholder="ログインメール" data-testid={`license-seat-edit-email-${s.id}`} className="h-9 text-sm" />
                          <Input type="password" value={editSeat.password} onChange={(e) => setEditSeat({ ...editSeat, password: e.target.value })}
                            placeholder="新しいパスワード（変更する場合のみ）" data-testid={`license-seat-edit-password-${s.id}`} className="h-9 text-sm" />
                          <div className="flex gap-2 justify-end">
                            <Button size="sm" variant="outline" onClick={() => setEditSeat(null)} className="h-8">キャンセル</Button>
                            <Button size="sm" disabled={busy} onClick={saveSeat} data-testid={`license-seat-edit-save-${s.id}`} className="h-8 bg-teal-700 hover:bg-teal-800">保存</Button>
                          </div>
                        </div>
                      )}
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
